import { memo, useMemo } from 'react';

/**
 * 字幕・講義メモ内の専門用語ハイライト。
 *
 * 辞書に載っている日本語見出し語を薄い点線下線で示し、タップで解説ポップアップを開く。
 * 「訳語は正しいのに意味がわからない」(現場フィードバック)への対応。
 */

export interface TermMatcher {
  /** 長い語優先の交替正規表現。用語が無いときは undefined。 */
  regex: RegExp | undefined;
  /** 日本語見出し語 → 中国語。 */
  terms: Record<string, string>;
}

/** 用語一覧からマッチャを組み立てる。長い語を先に置き、部分一致の横取りを防ぐ。 */
export function buildTermMatcher(terms: Record<string, string>): TermMatcher {
  const keys = Object.keys(terms)
    .filter((k) => k.length >= 2)
    .sort((a, b) => b.length - a.length);
  if (keys.length === 0) return { regex: undefined, terms };
  const escaped = keys.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return { regex: new RegExp(`(${escaped.join('|')})`, 'g'), terms };
}

/**
 * テキストを用語ヒットで分割して描画する。
 * partial(暫定字幕)には使わない想定 — 毎フレーム再計算になるため、確定分のみ。
 */
export const TermText = memo(function TermText({
  text,
  matcher,
  onTerm,
}: {
  text: string;
  matcher: TermMatcher;
  onTerm: (ja: string, zh: string) => void;
}): JSX.Element {
  const parts = useMemo(() => {
    if (!matcher.regex) return [text];
    return text.split(matcher.regex);
  }, [text, matcher]);

  return (
    <>
      {parts.map((part, i) =>
        // split の交替キャプチャは奇数インデックスにヒットが入る
        i % 2 === 1 ? (
          <span
            key={i}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onTerm(part, matcher.terms[part] ?? '');
            }}
            style={{
              borderBottom: '1px dotted currentColor',
              opacity: 0.95,
              cursor: 'pointer',
            }}
          >
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </>
  );
});
